import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, Eye, EyeOff, User as UserIcon, Mail } from "lucide-react";

function strengthScore(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

const STRENGTH_LABELS = ["Muy débil", "Débil", "Aceptable", "Fuerte", "Excelente"];
const STRENGTH_COLORS = [
  "bg-destructive/60",
  "bg-destructive",
  "bg-yellow-500",
  "bg-emerald-500",
  "bg-emerald-600",
];

export default function Account() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const isGoogleUser = user?.auth_provider === "google";
  const score = strengthScore(form.new_password);
  const strongEnough =
    form.new_password.length >= 8 &&
    /[A-Za-z]/.test(form.new_password) &&
    /\d/.test(form.new_password);
  const matches = form.new_password && form.new_password === form.confirm;
  const canSubmit = !isGoogleUser && strongEnough && matches && !!form.current_password;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      setForm({ current_password: "", new_password: "", confirm: "" });
      // Small delay then send agent-role users to dashboard, admin to /admin/news, buscador stays
      setTimeout(() => {
        if (user?.role === "agente") navigate("/dashboard");
        else if (user?.role === "admin") navigate("/admin/news");
      }, 400);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-10 pt-8 md:pt-16 pb-16" data-testid="account-page">
      <div className="mb-10">
        <div className="label-eyebrow text-secondary inline-flex items-center gap-2">
          <UserIcon className="h-3 w-3" /> Mi cuenta
        </div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">Ajustes de cuenta</h1>
        <p className="mt-2 text-muted-foreground">Gestiona tus credenciales y datos de acceso.</p>
      </div>

      {/* Profile summary */}
      <div className="border border-border rounded-sm p-5 mb-8" data-testid="account-profile">
        <div className="flex items-start gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-border" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-lg">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-serif text-2xl truncate">{user?.name}</div>
            <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> {user?.email}
            </div>
            <div className="mt-2 inline-flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {user?.role}
              </span>
              <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {user?.auth_provider === "google" ? "Google" : "Correo y contraseña"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Password change */}
      <div className="border border-border rounded-sm p-5 md:p-8" data-testid="password-card">
        <div className="flex items-center gap-3 mb-1">
          <KeyRound className="h-5 w-5 text-primary" />
          <h2 className="font-serif text-2xl">Cambiar contraseña</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Usa una contraseña única de al menos 8 caracteres, con letras y números.
        </p>

        {isGoogleUser ? (
          <div className="p-4 border border-dashed border-border rounded-sm text-sm text-muted-foreground" data-testid="google-user-notice">
            Tu cuenta inició sesión con <strong>Google</strong>. No existe una contraseña asociada. Administra tu clave desde tu cuenta de Google.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="password-form">
            <div>
              <label className="label-eyebrow block mb-2">Contraseña actual</label>
              <div className="relative">
                <Input
                  type={showCurrent ? "text" : "password"}
                  required
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  data-testid="current-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-current-visibility"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Nueva contraseña</label>
              <div className="relative">
                <Input
                  type={showNew ? "text" : "password"}
                  required
                  minLength={8}
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  data-testid="new-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-new-visibility"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.new_password && (
                <div className="mt-2" data-testid="strength-meter">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${i < score ? STRENGTH_COLORS[score] : "bg-muted"}`}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Fortaleza: <span className="font-medium">{STRENGTH_LABELS[score]}</span>
                  </p>
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">Mínimo 8 caracteres, combinando letras y números.</p>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Confirmar nueva contraseña</label>
              <Input
                type={showNew ? "text" : "password"}
                required
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="confirm-password"
                autoComplete="new-password"
              />
              {form.confirm && !matches && (
                <p className="mt-1 text-xs text-destructive" data-testid="confirm-mismatch">
                  Las contraseñas no coinciden.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={!canSubmit || saving}
                className="rounded-full btn-hover-lift"
                data-testid="submit-password-change"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Actualizar contraseña
              </Button>
              {canSubmit && !saving && (
                <span className="text-xs text-muted-foreground" data-testid="ready-hint">
                  Lista para guardarse
                </span>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
